/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */

// You can delete this file if you're not using it
import "./src/styles/style.scss"

// Import Swiper styles globally (once) to avoid redundant imports in each component
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import "swiper/css/scrollbar"


import "./src/components/Navbar/navbar.scss"; // Navbar styles

// Import PrimeReact styles globally
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.css';

import React, {Suspense, lazy} from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

import { AuthProvider } from './src/services/crm/context'
const Toaster = lazy(() => import("react-hot-toast").then(mod => ({ default: mod.Toaster })));

export const wrapRootElement = ({ element }) => {
  return (
    <AuthProvider>
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.GATSBY_RECAPTCHA_SITE_KEY || ""}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "body",
        }}
        useReCaptchaNet={true}
        useEnterprise={false}
        language="en"
      >
        {element}
        <Suspense fallback={null}>
          <Toaster position="top-right" reverseOrder={true} />
        </Suspense>
      </GoogleReCaptchaProvider>
    </AuthProvider>  
  )
}

// Remove ltr/rtl classes and dir attribute from body and html
function clearDir() {
  if (typeof document === 'undefined' || !document.body) return;
  document.body.classList.remove('ltr', 'rtl');
  document.documentElement.removeAttribute('dir');
}

// Check if pathname matches a destination plan page
function isDestination(path) {
  if (!path) return false;
  const parts = path.split('/').filter(Boolean);
  const idx = parts.indexOf('plan-your-visit');
  return idx !== -1 && parts.length > idx + 1;
}

// Watch for html dir attribute and remove it if set
function watchAndClearDir() {
  if (typeof document === 'undefined' || !document.documentElement) return;
  clearDir();
  if (window.__dirObserver) window.__dirObserver.disconnect();
  const observer = new MutationObserver(() => {
    if (document.documentElement.hasAttribute('dir')) {
      document.documentElement.removeAttribute('dir');
      document.body.classList.remove('ltr', 'rtl');
    }
  });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });
  window.__dirObserver = observer;
}

// Schedule multiple clearDir actions unless it's a special plan page
function scheduleClearDir(path) {
  if (path && isDestination(path)) return;
  clearDir();
  requestAnimationFrame(() => {
    requestAnimationFrame(clearDir);
  });
  [100, 300, 600].forEach(t => setTimeout(clearDir, t));
}

// Gatsby: called once on client load
export const onInitialClientRender = () => {
  if (typeof window === 'undefined') return;
  const path = window.location?.pathname || '';
  const mapPage = path === '/interactive-map' || path.endsWith('/interactive-map');
  if (!mapPage) {
    scheduleClearDir(path);
    watchAndClearDir();
  }
}

// Gatsby: called on every route update
export const onRouteUpdate = ({ location }) => {
  if (typeof window === 'undefined') return;
  const path = location?.pathname || window.location?.pathname || '';
  const mapPage = path === '/interactive-map' || path.endsWith('/interactive-map');
  const lang = path.split('/')[1] || 'en';

  if (!mapPage) {
    scheduleClearDir(path);
    watchAndClearDir();
    document.body.style.overflow = 'visible';
    document.documentElement.style.overflow = 'visible';

    const font = lang === 'en' ? 'FSKoopman' : 'SBF';
    document.body.style.fontFamily = font;
    document.documentElement.style.fontFamily = font;

    document.body.style.height = 'auto';
    document.documentElement.style.height = 'auto';
  }

  if (window.jQuery) {
    initializeComponents(window.jQuery);
    return;
  }

  const loadJQuery = () => new Promise((resolve) => {
    if (window.jQuery) return resolve(window.jQuery)
    const existing = document.querySelector('script[data-dynamic="jquery"]')
    if (existing) {
      existing.addEventListener('load', () => resolve(window.jQuery))
      return
    }
    const script = document.createElement('script')
    script.src = 'https://code.jquery.com/jquery-3.6.2.min.js'
    script.integrity = 'sha256-2krYZKh//PcchRtd+H+VyyQoZ/e3EcrkxhM8ycwASPA='
    script.crossOrigin = 'anonymous'
    script.async = true
    script.defer = true
    script.dataset.dynamic = 'jquery'
    script.addEventListener('load', () => resolve(window.jQuery))
    document.body.appendChild(script)
  })

  const triggerLoad = () => {
    ['mousedown','mousemove','keypress','scroll','touchstart'].forEach((evt) =>
      document.removeEventListener(evt, triggerLoad, listenerOpts)
    )
    loadJQuery().then(($) => initializeComponents($))
  }
  const listenerOpts = { once: true, passive: true }
  ;['mousedown','mousemove','keypress','scroll','touchstart'].forEach((evt) =>
    document.addEventListener(evt, triggerLoad, listenerOpts)
  )
  setTimeout(triggerLoad, 3000)
}

function initializeComponents() {
  function handleCustomTabs() {
    const tabs = document.querySelectorAll(".tabs li");
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        if (tab.classList.contains("active")) return;

        const tabsContainer = tab.closest(".tabs");
        const controls = tabsContainer?.dataset.controls;
        const title = tabsContainer?.dataset.title;
        const target = tab.dataset.tab;

        tabsContainer.querySelectorAll("li").forEach(t => t.classList.remove("active"));

        if (controls) {
          document.querySelectorAll(`${controls} > *`).forEach(el => el.classList.remove("active"));
        }

        if (title) {
          const titleEl = document.querySelector(title);
          if (titleEl) titleEl.textContent = tab.textContent;
        }

        document.querySelector(target)?.classList.add("active");
        tab.classList.add("active");
      });
    });
  }

  function initializeCustomCollapsiblefaq() {
    const attachEvents = () => {
      const headers = document.querySelectorAll(".custom-collapsible-faq .collapsible-header-faq");
      if (!headers.length) return;

      headers.forEach(header => {
        header.removeEventListener("click", header._faqClick);
        const clickHandler = () => {
          const body = header.nextElementSibling;
          const parent = header.closest(".custom-collapsible-faq");

          if (!header.classList.contains("open")) {
            header.classList.add("open");
            if (body) body.style.maxHeight = body.scrollHeight + "px";

            if (parent?.classList.contains("accordian")) {
              parent.parentElement.querySelectorAll(".custom-collapsible-faq").forEach(item => {
                if (item !== parent) {
                  const siblingBody = item.querySelector(".collapsible-body-faq");
                  const siblingHeader = item.querySelector(".collapsible-header-faq");
                  if (siblingBody) siblingBody.style.maxHeight = "0px";
                  siblingHeader?.classList.remove("open");
                }
              });
            }
          } else {
            if (body) body.style.maxHeight = "0px";
            header.classList.remove("open");
          }
        };
        header._faqClick = clickHandler;
        header.addEventListener("click", clickHandler);
      });
      document.querySelectorAll(".custom-collapsible-faq .collapsible-header-faq.open").forEach(header => {
        const body = header.nextElementSibling;
        if (body) body.style.maxHeight = body.scrollHeight + "px";
      });
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", attachEvents);
    } else {
      attachEvents();
    }
    window.addEventListener("load", attachEvents);
  }

  function initializeViewing() {
    const switchEl = document.querySelector(".switch-wrapper");
    if (!switchEl || switchEl.dataset.init) return;

    switchEl.dataset.init = "true";

    const update = (active) => {
      document.querySelector(".list-wrapper")?.classList.toggle("active", active);
      document.querySelector(".map-wrapper")?.classList.toggle("active", !active);
      document.querySelector(".discover")?.classList.toggle("active", active);
      document.querySelector(".list-view-text")?.classList.toggle("active", active);
      document.querySelector(".map-view-text")?.classList.toggle("active", !active);
    };

    update(switchEl.classList.contains("active"));

    switchEl.addEventListener("click", () => {
      const active = switchEl.classList.toggle("active");
      update(active);
    });
  }

  handleCustomTabs();
  initializeCustomCollapsiblefaq();
  initializeViewing();
}
